// RTM-AI-Agent API Service
import type { 
  ApiResponse, 
  LoginRequest, 
  LoginResponse,
  CreateProjectRequest,
  Project,
  SaaSUser,
  RTMSession,
  UsageStats
} from '../types';

export class ApiService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    // 相対パスを使用してViteプロキシ経由でバックエンドにアクセス
    // ハードコードされたURLは使用しない（CORS・ポート問題を回避）
    this.baseUrl = '';
  }

  // Auth methods
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('rtm_token', token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('rtm_token');
    }
    return this.token;
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('rtm_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    _isRetry = false
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options.headers || {}),
      },
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401 && !_isRetry) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return this.request<T>(endpoint, options, true);
        }
      }

      const data = await response.json().catch(() => ({} as Record<string, unknown>));

      if (!response.ok) {
        return {
          success: false,
          error: (data as { message?: string }).message || `HTTP ${response.status}`,
          code: response.status,
        };
      }

      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // Authentication APIs
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData: {
    name: string;
    email: string;
    password: string;
  }): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({
        email: userData.email,
        password: userData.password,
        full_name: userData.name,
      }),
    });
  }

  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  getCurrentUser(): ApiResponse<SaaSUser> {
    // バックエンドに /v1/auth/me エンドポイントは存在しないため、
    // ネットワークリクエストは行わず localStorage から直接ユーザーデータを読み取る。
    // dev/mock 環境ではログイン時に localStorage へユーザーデータが保存される。
    const sources = [
      localStorage.getItem('rtm-saas-storage'),
      localStorage.getItem('rtm_user'),
      localStorage.getItem('auth_user'),
    ];
    for (const stored of sources) {
      if (!stored) continue;
      try {
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        // Zustand persist 形式: { state: { user: ... } }
        const user =
          (parsed.state as Record<string, unknown> | undefined)?.user ?? parsed;
        if (user && typeof user === 'object' && 'id' in user) {
          return { success: true, data: user as SaaSUser };
        }
      } catch {
        // パース失敗は無視して次のソースへ
      }
    }
    return { success: false, error: 'User not found', code: 404 };
  }

  async logout(): Promise<ApiResponse<{ message: string }>> {
    const result = await this.request<{ message: string }>('/v1/auth/logout', {
      method: 'POST',
    });
    this.clearToken();
    return result;
  }

  updateProfile(profileData: {
    name: string;
    email: string;
    company?: string;
    position?: string;
    timezone: string;
    language: string;
  }): ApiResponse<SaaSUser> {
    // バックエンドに /v1/auth/profile エンドポイントは存在しないため、
    // ネットワークリクエストは行わず localStorage のユーザーデータを直接更新する。
    const currentResult = this.getCurrentUser();
    const base: SaaSUser = currentResult.success && currentResult.data
      ? currentResult.data
      : {
          id: 'local-user',
          name: profileData.name,
          email: profileData.email,
          plan: 'starter',
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
          isActive: true,
        };

    const updated: SaaSUser = { ...base, ...profileData };

    // rtm_user キーとして保存（他の箇所でも参照されるシンプルなキー）
    localStorage.setItem('rtm_user', JSON.stringify(updated));

    // Zustand persist ストアにも反映する（存在する場合のみ）
    const persistRaw = localStorage.getItem('rtm-saas-storage');
    if (persistRaw) {
      try {
        const persist = JSON.parse(persistRaw) as Record<string, unknown>;
        if (persist.state && typeof persist.state === 'object') {
          (persist.state as Record<string, unknown>).user = updated;
          localStorage.setItem('rtm-saas-storage', JSON.stringify(persist));
        }
      } catch {
        // パース失敗は無視
      }
    }

    return { success: true, data: updated };
  }

  // Project Management APIs
  async getProjects(): Promise<ApiResponse<Project[]>> {
    return this.request<Project[]>('/api/projects');
  }

  async getProject(id: string): Promise<ApiResponse<Project>> {
    return this.request<Project>(`/api/projects/${id}`);
  }

  async createProject(projectData: CreateProjectRequest): Promise<ApiResponse<Project>> {
    return this.request<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  async updateProject(id: string, updates: Partial<Project>): Promise<ApiResponse<Project>> {
    return this.request<Project>(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteProject(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/api/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // API Key Management (Enhanced with new backend endpoints)
  async generateApiKey(
    projectId: string, 
    name: string, 
    permissions?: string[]
  ): Promise<ApiResponse<{
    id: string;
    name: string;
    projectId: string;
    apiKey: string;
    createdAt: string;
  }>> {
    return this.request<{
      id: string;
      name: string;
      projectId: string;
      apiKey: string;
      createdAt: string;
    }>(`/api/projects/${projectId}/keys`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async revokeApiKey(projectId: string, keyId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/api/projects/${projectId}/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  async getApiKeys(projectId: string): Promise<ApiResponse<Array<{
    id: string;
    name: string;
    projectId: string;
    keyPreview: string; // masked key for display
    createdAt: string;
    lastUsed?: string;
  }>>> {
    return this.request<Array<{
      id: string;
      name: string;
      projectId: string;
      keyPreview: string;
      createdAt: string;
      lastUsed?: string;
    }>>(`/api/projects/${projectId}/keys`);
  }

  // Session Management APIs (Enhanced with new backend endpoints)
  async getSessions(projectId?: string): Promise<ApiResponse<RTMSession[]>> {
    const endpoint = projectId ? `/v1/sessions/?project=${projectId}` : '/v1/sessions/';
    return this.request<RTMSession[]>(endpoint);
  }

  async getSession(sessionId: string): Promise<ApiResponse<RTMSession>> {
    return this.request<RTMSession>(`/v1/sessions/${sessionId}`);
  }

  async createSession(sessionData: {
    projectId?: string;
    meetingName?: string;
    participantCount?: number;
    language?: string;
    industryContext?: string;
    agentConfig?: {
      enableListening?: boolean;
      enableAnalysis?: boolean;
      enableResearch?: boolean;
      enableSuggestions?: boolean;
      enableMemory?: boolean;
      analysisDepth?: 'shallow' | 'medium' | 'deep';
      suggestionFrequency?: 'minimal' | 'balanced' | 'active';
    };
  }): Promise<ApiResponse<{
    sessionId: string;
    websocketUrl: string;
    createdAt: string;
  }>> {
    return this.request<{
      sessionId: string;
      websocketUrl: string;
      createdAt: string;
    }>('/v1/sessions/', {
      method: 'POST',
      body: JSON.stringify({
        meeting_name: sessionData.meetingName,
        participant_count: sessionData.participantCount || 1,
        language: sessionData.language || 'ja-JP',
        industry_context: sessionData.industryContext,
        agent_config: sessionData.agentConfig ? {
          enable_listening: sessionData.agentConfig.enableListening ?? true,
          enable_analysis: sessionData.agentConfig.enableAnalysis ?? true,
          enable_research: sessionData.agentConfig.enableResearch ?? true,
          enable_suggestions: sessionData.agentConfig.enableSuggestions ?? true,
          enable_memory: sessionData.agentConfig.enableMemory ?? true,
          analysis_depth: sessionData.agentConfig.analysisDepth || 'medium',
          suggestion_frequency: sessionData.agentConfig.suggestionFrequency || 'balanced',
        } : undefined,
      }),
    });
  }

  async getSessionDetail(sessionId: string): Promise<ApiResponse<{
    sessionId: string;
    status: string;
    createdAt: string;
    endedAt?: string;
    participantCount: number;
    meetingName?: string;
    language: string;
    totalDurationSeconds?: number;
    audioProcessedSeconds?: number;
  }>> {
    return this.request(`/v1/sessions/${sessionId}`);
  }

  async getSessionStatus(sessionId: string): Promise<ApiResponse<{
    sessionId: string;
    status: string;
    currentParticipants: number;
    lastActivity?: string;
  }>> {
    return this.request(`/v1/sessions/${sessionId}/status`);
  }

  async endSession(sessionId: string): Promise<ApiResponse<{
    message: string;
    sessionId: string;
    terminatedAt: string;
  }>> {
    return this.request<{
      message: string;
      sessionId: string;
      terminatedAt: string;
    }>(`/v1/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  // Usage & Analytics APIs
  async getUsageStats(
    projectId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<UsageStats>> {
    const params = new URLSearchParams();
    if (projectId) params.append('project', projectId);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);

    const endpoint = `/api/usage${params.toString() ? '?' + params.toString() : ''}`;
    return this.request<UsageStats>(endpoint);
  }

  async getDashboardStats(): Promise<ApiResponse<{
    activeSessions: number;
    totalUsersToday: number;
    successRate: number;
    avgSessionDuration: number;
    realtimeMetrics: {
      currentConnections: number;
      messagesPerSecond: number;
      avgLatency: number;
      errorRate: number;
    };
  }>> {
    return this.request('/v1/metrics/stats');
  }

  async exportUsageData(
    format: 'csv' | 'excel' | 'pdf',
    params: {
      projectId?: string;
      startDate?: string;
      endDate?: string;
      industry?: string;
    } = {}
  ): Promise<ApiResponse<{ downloadUrl: string }>> {
    return this.request<{ downloadUrl: string }>('/api/usage/export', {
      method: 'POST',
      body: JSON.stringify({ format, ...params }),
    });
  }

  // Billing APIs
  async getBillingInfo(): Promise<ApiResponse<{
    currentPlan: string;
    usage: number;
    limit: number;
    billingCycle: string;
    nextBillingDate: string;
    cost: number;
  }>> {
    return this.request('/api/billing/info');
  }

  async getInvoices(): Promise<ApiResponse<Array<{
    id: string;
    date: string;
    amount: number;
    status: string;
    downloadUrl: string;
  }>>> {
    return this.request('/api/billing/invoices');
  }

  async updatePaymentMethod(paymentMethodId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request('/api/billing/payment-method', {
      method: 'PUT',
      body: JSON.stringify({ paymentMethodId }),
    });
  }

  // Ephemeral Token APIs (for WebSocket authentication)
  async createEphemeralToken(
    projectId: string,
    ttlSeconds: number = 3600
  ): Promise<ApiResponse<{
    token: string;
    expires_at: string;
  }>> {
    return this.request<{
      token: string;
      expires_at: string;
    }>('/v1/auth/ephemeral-token', {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        ttl_seconds: ttlSeconds,
      }),
      headers: {
        'X-API-Key': this.getApiKey(),
      },
    });
  }

  // API Key management for direct API authentication
  setApiKey(apiKey: string): void {
    localStorage.setItem('rtm_api_key', apiKey);
  }

  getApiKey(): string | null {
    return localStorage.getItem('rtm_api_key');
  }

  clearApiKey(): void {
    localStorage.removeItem('rtm_api_key');
  }

  /**
   * Workspace / Onboarding API 専用: レスポンスを直接 T として返すシンプルな fetch ラッパー。
   * 既存の ApiResponse<T> ラッパーを通さずにデータを返すため、新規 API クライアントから利用する。
   *
   * 401 を受けた場合は refresh_token で access_token を再取得し、元リクエストを 1 回だけリトライする。
   * refresh も失敗した場合は HTTP 401 エラーをスローする（呼び出し元が logout 処理を担う）。
   */
  async fetch<T>(endpoint: string, options: RequestInit = {}, _isRetry = false): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }
    const config: RequestInit = {
      ...options,
      headers: { ...defaultHeaders, ...(options.headers as Record<string, string> | undefined ?? {}) },
    };
    const response = await window.fetch(url, config);

    // 401 受信 + 初回のみ refresh を試みてリトライ
    if (response.status === 401 && !_isRetry) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        return this.fetch<T>(endpoint, options, true);
      }
      // refresh 失敗 → そのまま 401 エラーをスロー
      const text = await response.text();
      throw new Error(`HTTP 401: ${text}`);
    }

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }
    // 204 No Content
    if (response.status === 204) {
      return undefined as unknown as T;
    }
    return response.json() as Promise<T>;
  }

  /**
   * localStorage の rtm_refresh_token を使って新しい access_token を取得する。
   * 成功した場合は新しい access_token をセットして true を返す。
   * 失敗（refresh_token なし / バックエンドエラー / ネットワークエラー）の場合は false を返す。
   */
  async refreshAccessToken(): Promise<boolean> {
    const rt = localStorage.getItem('rtm_refresh_token');
    if (!rt) return false;
    try {
      const r = await window.fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!r.ok) return false;
      const data = await r.json() as { access_token?: string; refresh_token?: string };
      if (data.access_token) {
        this.setToken(data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('rtm_refresh_token', data.refresh_token);
        }
        return true;
      }
    } catch {
      // ネットワークエラー等は握りつぶして false を返す
    }
    return false;
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<{
    status: string;
    version: string;
    timestamp: string;
  }>> {
    return this.request('/health');
  }
}

// Singleton instance
const apiService = new ApiService();

export default apiService;

// Named export for new workspace/onboarding API clients
export { apiService };